import { Injectable, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';

import { environment } from 'src/environments/environment';
import { LogsService } from 'src/app/abertura-conta/config/service/logs/logs.service';
import { AnalyticsEventType, IAnalyticsParams } from './analytics.model';
import { AnalyticsEnumGeneral } from './analytics.enum';

/**
 * Serviço responsável pelo tagueamento GA4 da jornada Onboarding Agro PF.
 *
 * Carrega o SDK Analytics Web do Itaú de forma assíncrona, configura os
 * parâmetros padrão e expõe métodos para disparar eventos screen_view e click
 * conforme a TechSpec 15213 e 15278.
 *
 * @see {@link https://portaltoolkitanalytics.cloud.itau.com.br/techspecGa4?documento=15213}
 * @see {@link https://portaltoolkitanalytics.cloud.itau.com.br/techspecGa4?documento=15278}
 */
@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private readonly logsService = inject(LogsService);
  private readonly document = inject(DOCUMENT);

  /** Guard para evitar inicialização duplicada por sessão */
  private initialized = false;

  /** Instância do SDK retornada por SDKAnalytics({ gtm }) */
  private sdk: any = null;

  /**
   * Inicializa o SDK Analytics Web do Itaú injetando o script de forma assíncrona
   * no `document.head`. Idempotente: chamadas subsequentes são ignoradas.
   *
   * Após carregamento bem-sucedido, chama `window.SDKAnalytics` com o GTM ID
   * e armazena a instância retornada para uso em trackScreenView e trackClick.
   */
  init(): void {
    if (this.initialized) {
      return;
    }

    if (!environment.analyticsGtmId) {
      this.logsService.logError('analytics:init-failure', { reason: 'analyticsGtmId-missing' });
      return;
    }

    this.initialized = true;

    const script = this.document.createElement('script') as HTMLScriptElement;
    script.src = environment.analyticsSDKUrl ?? '';
    script.async = true;

    script.onload = () => {
      try {
        const factory = (window as any)['SDKAnalytics'];
        if (factory) {
          this.sdk = factory({ gtm: environment.analyticsGtmId, fullstory: environment.fullstory });
          console.log("SDK Analytics Web do Itaú carregado com sucesso:", this.sdk);
        }
        this.setDefaultParameters();
      } catch (error) {
        console.log("Falha ao carregar SDK Analytics Web do Itaú:", error);
        this.logsService.logError('analytics:init-failure', { gtm: environment.analyticsGtmId });
      }
    };

    script.onerror = () => {
      this.logsService.logError('analytics:init-failure', { gtm: environment.analyticsGtmId });
    };

    this.document.head.appendChild(script);
  }

  /**
   * Configura os parâmetros padrão de negócio obrigatórios conforme TechSpec 15213 e 15278.
   * Deve ser chamado após o carregamento bem-sucedido do SDK.
   */
  private setDefaultParameters(): void {
    try {
      this.sdk?.setDefaultParameters({
        analytics: {
          parameters: {
            business: 'pj',
            business_channel: 'onboarding-atacado',
            environment: 'nao-logado',
          },
        },
      });
    } catch (error) {
      this.logsService.logError('analytics:set-default-parameters-failure', { gtm: environment.analyticsGtmId });
    }
  }

  /**
   * Dispara um evento `screen_view` com o `custom_path` da tela.
   *
   * @param customPath - Caminho customizado da tela conforme TechSpec 15213 e 15278
   *   (ex: `/abertura-de-conta-corrente/onboarding-agro-pf-tradicional/tela-inicial`)
   */
  trackScreenView(customPath: string): void {
    console.info(`Tentando disparar evento trackScreenView: customPath=${customPath}`);
    try {
      if (!this.sdk) {
        this.logsService.logError('analytics:track-failure', {
          event_name: AnalyticsEventType.SCREEN_VIEW,
          custom_path: customPath,
        } as IAnalyticsParams);

        console.error(`SDK Analytics não disponível para trackScreenView: customPath=${customPath}`);
        return;
      }
      this.sdk.track({
        analytics: {
          event_name: AnalyticsEventType.SCREEN_VIEW,
          parameters: { 
            custom_path: customPath, 
            implementation_team: AnalyticsEnumGeneral.IMPLEMENTATION_TEAM 
          } as IAnalyticsParams,
        },
      });
      console.info(`Evento trackScreenView disparado: ${customPath}`);
    } catch (error) {
      this.logsService.logError('analytics:track-failure', {
        event_name: AnalyticsEventType.SCREEN_VIEW,
        custom_path: customPath,
      } as IAnalyticsParams);
      console.error(`Falha ao disparar evento trackScreenView: ${customPath}`, error);
    }
  }

  /**
   * Dispara um evento `click` com o detalhe e `custom_path` da tela.
   *
   * @param detail - Identificador do elemento clicado conforme TechSpec 15213 e 15278
   *   (ex: `botao:adicionar-novo-cotitular`)
   * @param customPath - Caminho customizado da tela conforme TechSpec 15213 e 15278
   */
  trackClick(detail: string, customPath: string): void {
    console.info(`Tentando disparar evento trackClick: detail=${detail}, customPath=${customPath}`);
    try {
      if (!this.sdk) {
        this.logsService.logError('analytics:track-failure', {
          event_name: AnalyticsEventType.CLICK,
          detail,
          custom_path: customPath,
        } as IAnalyticsParams);

        console.error(`SDK Analytics não disponível para trackClick: detail=${detail}, customPath=${customPath}`);
        return;
      }
      this.sdk.track({
        analytics: {
          event_name: AnalyticsEventType.CLICK,
          parameters: { detail, custom_path: customPath, implementation_team: AnalyticsEnumGeneral.IMPLEMENTATION_TEAM } as IAnalyticsParams,
        },
      });
      console.info(`Evento trackClick disparado: detail=${detail}, customPath=${customPath}`);
    } catch (error) {
      this.logsService.logError('analytics:track-failure', {
        event_name: AnalyticsEventType.CLICK,
        detail,
        custom_path: customPath,
      } as IAnalyticsParams);
      console.error(`Falha ao disparar evento trackClick: detail=${detail}, customPath=${customPath}`, error);
    }
  }
}
